import { useEffect, useState, useMemo, useRef, Fragment } from "react";
import {
  Mail, RefreshCw, AlertTriangle, Search, CheckCircle2, Upload, FileSpreadsheet,
  MessageCircle, Scale, Building2, Printer, Info, X, Send, ShieldCheck,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiError, isSuperRole } from "../../utils/auth";
import { normalizeWANumberLocal } from "../../utils/fonnte";

// ─── Status config ──────────────────────────────────────────────────────────
const STATUS = {
  ALL:          { label: "Semua",        bg: "bg-indigo-600",  text: "text-white",        dot: "bg-white",      hex: "#4f46e5" },
  SELESAI:      { label: "Selesai",      bg: "bg-emerald-50",  text: "text-emerald-700",  dot: "bg-emerald-500", hex: "#059669" },
  DITANGGAPI:   { label: "Ditanggapi",   bg: "bg-blue-50",     text: "text-blue-700",     dot: "bg-blue-500",   hex: "#1d4ed8" },
  PROSES:       { label: "Diproses",     bg: "bg-amber-50",    text: "text-amber-700",    dot: "bg-amber-400",  hex: "#b45309" },
  DITANGGUHKAN: { label: "Ditangguhkan", bg: "bg-slate-100",   text: "text-slate-600",    dot: "bg-slate-400",  hex: "#475569" },
  DITERIMA:     { label: "Diterima",     bg: "bg-violet-50",   text: "text-violet-700",   dot: "bg-violet-400", hex: "#6d28d9" },
};

// Urutan tetap untuk rekap (chip, pesan WA, maupun PDF) supaya laporan konsisten.
const STATUS_ORDER = ["SELESAI", "DITANGGAPI", "PROSES", "DITERIMA", "DITANGGUHKAN"];

// Gradien baris — lembut, hanya sebagai penanda status (bukan blok warna penuh).
const ROW_TINT = {
  SELESAI:      "linear-gradient(90deg, rgba(16,185,129,0.10) 0%, rgba(255,255,255,0) 38%)",
  DITANGGAPI:   "linear-gradient(90deg, rgba(59,130,246,0.10) 0%, rgba(255,255,255,0) 38%)",
  PROSES:       "linear-gradient(90deg, rgba(245,158,11,0.12) 0%, rgba(255,255,255,0) 38%)",
  DITANGGUHKAN: "linear-gradient(90deg, rgba(100,116,139,0.10) 0%, rgba(255,255,255,0) 38%)",
  DITERIMA:     "linear-gradient(90deg, rgba(139,92,246,0.10) 0%, rgba(255,255,255,0) 38%)",
};

const URGENCY_COLOR = {
  Rahasia: "text-red-600 font-bold",
  Segera:  "text-orange-500 font-bold",
  Penting: "text-amber-600 font-semibold",
};

// ─── Pengelompokan sumber surat ─────────────────────────────────────────────
// APH = Aparat Penegak Hukum (Kepolisian & Kejaksaan). Dicocokkan dari kolom
// "Asal Surat" — sengaja longgar supaya varian penulisan tetap tertangkap
// (Polres / Polda / Kejari / Kejaksaan Negeri / Sat.Reskrim, dst).
const APH_PATTERN = /(kejaksaan|kejari|kejati|kepolisian|polres|polda|polsek|polri|reskrim|polisi)/i;

function isAPH(row) {
  return APH_PATTERN.test(row?.asalSurat || "");
}

function escapeHtml(v) {
  return String(v == null ? "" : v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function tanggalPanjang() {
  return new Date().toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
}

// Nomor HP Indonesia yang sah untuk WhatsApp: 62 + 8xxxxxxxxx (10–14 digit total).
const POLA_NOMOR_WA = /^628\d{7,11}$/;

// 6281234567890 → +62 812-3456-7890 (dibaca ulang sebelum kirim, biar ketahuan
// kalau nomornya salah ketik di menu Pengaturan).
function formatNomorWA(n) {
  const s = String(n || "");
  if (!s.startsWith("62")) return s;
  const sisa = s.slice(2);
  const a = sisa.slice(0, 3), b = sisa.slice(3, 7), c = sisa.slice(7);
  return `+62 ${[a, b, c].filter(Boolean).join("-")}`;
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function DumasTable() {
  const { user } = useAuth();
  const canUpload = isSuperRole(user);
  const fileInputRef = useRef(null);

  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [uploading, setUploading] = useState(false);
  const [waBusy, setWaBusy]     = useState(false);
  const [error, setError]       = useState(null);
  const [uploadMsg, setUploadMsg] = useState(null);
  const [infoMsg, setInfoMsg]   = useState(null);
  const [waTarget, setWaTarget] = useState(null); // {name, role, noWAAsli, nomor}
  const [filter, setFilter]     = useState("ALL");
  const [search, setSearch]     = useState("");
  const [ts, setTs]             = useState(null);
  const [uploadedBy, setUploadedBy] = useState(null);

  function fetchData() {
    setLoading(true); setError(null);
    api
      .get("/dumas")
      .then(({ data }) => {
        setRows(data.data || []);
        setTs(data.updatedAt ? new Date(data.updatedAt) : null);
        setUploadedBy(data.uploadedBy || null);
        setLoading(false);
      })
      .catch((e) => {
        setError(formatApiError(e?.response?.data?.detail) || "Gagal memuat data.");
        setLoading(false);
      });
  }

  useEffect(() => { fetchData(); }, []);

  function handleFileSelect(e) {
    const file = e.target.files?.[0];
    e.target.value = ""; // supaya file yang sama bisa dipilih ulang nanti
    if (!file) return;

    setUploading(true);
    setUploadMsg(null);
    setInfoMsg(null);
    setError(null);

    const formData = new FormData();
    formData.append("file", file);

    api
      .post("/dumas/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(({ data }) => {
        setUploadMsg(data.message || "Berhasil diunggah.");
        fetchData();
      })
      .catch((e) => {
        setError(formatApiError(e?.response?.data?.detail) || "Gagal mengunggah file.");
      })
      .finally(() => setUploading(false));
  }

  const counts = useMemo(() => {
    const c = { SELESAI: 0, DITANGGAPI: 0, PROSES: 0, DITANGGUHKAN: 0, DITERIMA: 0 };
    rows.forEach((r) => { if (r.status in c) c[r.status]++; });
    return c;
  }, [rows]);

  const sumber = useMemo(() => {
    const aph = rows.filter(isAPH).length;
    return { aph, nonAph: rows.length - aph };
  }, [rows]);

  const filtered = useMemo(() => {
    let list = filter === "ALL" ? rows : rows.filter((r) => r.status === filter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (r) =>
          r.objek.toLowerCase().includes(q) ||
          r.asalSurat.toLowerCase().includes(q) ||
          r.materi.toLowerCase().includes(q) ||
          r.noSurat.toLowerCase().includes(q)
      );
    }
    return list;
  }, [rows, filter, search]);

  // Dua kelompok tampilan: dari APH, dan selain APH.
  const groups = useMemo(() => ([
    {
      key: "APH",
      label: "Dari APH (Kepolisian / Kejaksaan)",
      Icon: Scale,
      headGradient: "linear-gradient(90deg, #ffe4e6 0%, #fff1f2 45%, #ffffff 100%)",
      headText: "text-rose-700",
      accent: "#f43f5e",
      items: filtered.filter(isAPH),
    },
    {
      key: "NON_APH",
      label: "Selain APH",
      Icon: Building2,
      headGradient: "linear-gradient(90deg, #e0f2fe 0%, #f0f9ff 45%, #ffffff 100%)",
      headText: "text-sky-700",
      accent: "#0ea5e9",
      items: filtered.filter((r) => !isAPH(r)),
    },
  ]), [filtered]);

  const sedangDifilter = filter !== "ALL" || search.trim() !== "";

  // ─── Laporan WhatsApp ke Irban ───────────────────────────────────────────
  function buildLaporanWA() {
    const ditangani = counts.SELESAI + counts.DITANGGAPI;
    const pending = counts.DITERIMA + counts.DITANGGUHKAN;

    const baris = [
      "Yth. Pak Irban Khusus",
      "",
      `Ijin menyampaikan perkembangan penanganan Pengaduan Masyarakat sampai dengan ${tanggalPanjang()}.`,
      "",
      "*REKAP STATUS PENANGANAN*",
      `Total surat masuk: ${rows.length}`,
    ];
    STATUS_ORDER.forEach((k) => {
      if (counts[k] > 0) baris.push(`• ${STATUS[k].label}: ${counts[k]}`);
    });
    baris.push(
      "",
      "*ASAL PENGADUAN*",
      `• Dari APH (Kepolisian/Kejaksaan): ${sumber.aph}`,
      `• Selain APH: ${sumber.nonAph}`,
      "",
      "*RINGKASAN*",
      `${ditangani} surat sudah ditangani, ${counts.PROSES} dalam proses, ${pending} belum ditindaklanjuti.`,
      "",
      "Rincian lengkap per surat terlampir dalam file PDF.",
      "",
      "Demikian laporan ini kami sampaikan sebagai bahan lebih lanjut.",
      "",
      "Penanggung jawab kegiatan Dumas,",
      "Maya, Muji"
    );
    return baris.join("\n");
  }

  // ─── Lampiran PDF (dicetak lewat dialog print browser) ───────────────────
  function buildLaporanHTML() {
    const tgl = tanggalPanjang();
    const chip = (label, nilai, warna) =>
      `<div class="kotak" style="border-color:${warna}22;background:linear-gradient(135deg, ${warna}14 0%, #ffffff 75%)">
         <div class="kotak-nilai" style="color:${warna}">${nilai}</div>
         <div class="kotak-label">${escapeHtml(label)}</div>
       </div>`;

    const rekapStatus = STATUS_ORDER.filter((k) => counts[k] > 0)
      .map((k) => chip(STATUS[k].label, counts[k], STATUS[k].hex))
      .join("");

    const barisKelompok = (g) => {
      if (!g.items.length) return "";
      const isi = g.items
        .map(
          (r, i) => `
        <tr class="${i % 2 ? "genap" : ""}">
          <td class="tengah mono">${escapeHtml(r.no)}</td>
          <td class="kecil">${escapeHtml(r.tanggal || r.tglTerima)}</td>
          <td class="mono kecil">${escapeHtml(r.noSurat)}</td>
          <td><div class="tebal">${escapeHtml(r.objek)}</div><div class="samar">${escapeHtml(r.asalSurat)}</div></td>
          <td>${escapeHtml(r.materi)}</td>
          <td class="kecil">${escapeHtml(r.keterangan)}</td>
          <td class="tengah">
            <span class="badge" style="color:${STATUS[r.status]?.hex || "#475569"};border-color:${STATUS[r.status]?.hex || "#475569"}33;background:${STATUS[r.status]?.hex || "#475569"}12">
              ${escapeHtml(STATUS[r.status]?.label || r.status)}
            </span>
            ${r.sifat && r.sifat !== "Biasa" && r.sifat !== "-" ? `<div class="sifat">⚑ ${escapeHtml(r.sifat)}</div>` : ""}
          </td>
        </tr>`
        )
        .join("");

      return `
        <tr class="kelompok" style="background:${g.headGradient}">
          <td colspan="7" style="border-left:3px solid ${g.accent}">
            ${escapeHtml(g.label)} <span class="jml">(${g.items.length} surat)</span>
          </td>
        </tr>
        ${isi}`;
    };

    return `<!DOCTYPE html>
<html lang="id"><head><meta charset="utf-8" />
<title>Rekap Surat Masuk Dumas - ${escapeHtml(tgl)}</title>
<style>
  @page { size: A4 landscape; margin: 12mm 10mm; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { font-family: "Segoe UI", Arial, sans-serif; color:#1e293b; margin:0; font-size:9.5px; }
  .kop { background:linear-gradient(135deg,#4338ca 0%,#6366f1 55%,#818cf8 100%); color:#fff;
         padding:14px 18px; border-radius:10px; margin-bottom:12px; }
  .kop h1 { margin:0; font-size:15px; letter-spacing:.2px; }
  .kop h2 { margin:2px 0 0; font-size:10.5px; font-weight:500; color:#c7d2fe; }
  .kop .tgl { margin-top:6px; font-size:9.5px; color:#e0e7ff; }
  .rekap { display:flex; flex-wrap:wrap; gap:7px; margin-bottom:12px; }
  .kotak { border:1px solid; border-radius:8px; padding:7px 12px; min-width:88px; }
  .kotak-nilai { font-size:16px; font-weight:700; line-height:1.1; }
  .kotak-label { font-size:8.5px; color:#64748b; margin-top:1px; text-transform:uppercase; letter-spacing:.4px; }
  table { width:100%; border-collapse:collapse; }
  thead th { background:linear-gradient(135deg,#eef2ff,#e0e7ff); color:#3730a3; font-size:8px;
             text-transform:uppercase; letter-spacing:.5px; padding:6px 5px; text-align:left;
             border-bottom:1px solid #c7d2fe; }
  tbody td { padding:5px; border-bottom:1px solid #eef2f7; vertical-align:top; line-height:1.35; }
  tbody tr.genap td { background:#fafbff; }
  tr.kelompok td { background:#f8fafc; font-size:9px; font-weight:700; text-transform:uppercase;
                   letter-spacing:.6px; padding:6px 8px; color:#334155; }
  tr.kelompok .jml { font-weight:500; text-transform:none; letter-spacing:0; color:#64748b; }
  .tengah { text-align:center; } .mono { font-family:Consolas,monospace; }
  .kecil { font-size:8.5px; color:#475569; } .tebal { font-weight:600; }
  .samar { color:#94a3b8; font-size:8px; margin-top:1px; }
  .badge { display:inline-block; border:1px solid; border-radius:999px; padding:1px 6px;
           font-size:8px; font-weight:700; white-space:nowrap; }
  .sifat { font-size:7.5px; color:#b91c1c; margin-top:2px; }
  .ttd { margin-top:18px; font-size:9.5px; page-break-inside:avoid; }
  .ttd .nama { margin-top:26px; font-weight:600; }
  .catatan { margin-top:10px; font-size:8px; color:#94a3b8; }
  thead { display:table-header-group; } tr { page-break-inside:avoid; }
</style></head>
<body onload="window.print()">
  <div class="kop">
    <h1>Rekap Surat Masuk Pengaduan Masyarakat (Dumas)</h1>
    <h2>Inspektorat Daerah Kabupaten Tasikmalaya &middot; Inspektur Pembantu Khusus</h2>
    <div class="tgl">Perkembangan penanganan sampai dengan ${escapeHtml(tgl)}</div>
  </div>

  <div class="rekap">
    ${chip("Total Surat", rows.length, "#4f46e5")}
    ${rekapStatus}
    ${chip("Dari APH", sumber.aph, "#e11d48")}
    ${chip("Selain APH", sumber.nonAph, "#0284c7")}
  </div>

  <table>
    <thead><tr>
      <th style="width:26px">No</th>
      <th style="width:62px">Tanggal</th>
      <th style="width:104px">No. Surat</th>
      <th style="width:150px">Objek / Asal Surat</th>
      <th style="width:200px">Materi Pengaduan</th>
      <th>Keterangan / Perkembangan</th>
      <th style="width:74px">Status</th>
    </tr></thead>
    <tbody>${groups.map(barisKelompok).join("")}</tbody>
  </table>

  ${sedangDifilter
      ? `<div class="catatan">Catatan: laporan ini mencetak ${filtered.length} dari ${rows.length} surat sesuai filter/pencarian yang sedang aktif.</div>`
      : ""}

  <div class="ttd">
    Demikian laporan ini kami sampaikan sebagai bahan lebih lanjut.
    <div class="nama">Penanggung jawab kegiatan Dumas,<br />Maya, Muji</div>
  </div>
</body></html>`;
  }

  function handleCetakPDF() {
    setError(null);
    if (!rows.length) return;
    const w = window.open("", "_blank");
    if (!w) {
      setError("Jendela cetak diblokir browser. Izinkan pop-up untuk situs ini, lalu coba lagi.");
      return;
    }
    w.document.write(buildLaporanHTML());
    w.document.close();
    return w;
  }

  // Menyiapkan tujuan kirim — TIDAK membuka apa pun. Nomor diambil hanya dari
  // data tim di menu Pengaturan, lalu ditampilkan dulu untuk dicek pengguna.
  async function handleKirimWA() {
    setWaBusy(true);
    setError(null);
    setUploadMsg(null);
    setInfoMsg(null);
    try {
      const { data: users } = await api.get("/users");
      const daftar = Array.isArray(users) ? users : [];

      // Hanya pengguna berperan IRBAN yang boleh jadi tujuan — tidak ada
      // fallback ke peran lain, supaya laporan tidak pernah nyasar.
      const kandidat = daftar.filter((u) => u.role === "IRBAN");
      if (kandidat.length === 0) {
        throw new Error(
          "Tidak ada pengguna dengan peran IRBAN pada daftar tim. Periksa menu Pengaturan."
        );
      }
      const cocokNama = kandidat.filter((u) =>
        (u.name || "").toLowerCase().includes("ara sundara")
      );
      if (cocokNama.length === 0 && kandidat.length > 1) {
        throw new Error(
          `Ditemukan ${kandidat.length} pengguna berperan IRBAN (${kandidat
            .map((u) => u.name)
            .join(", ")}). Pastikan nama Ara Sundara terdaftar agar tujuan tidak keliru.`
        );
      }
      const irban = cocokNama[0] || kandidat[0];

      const asli = (irban.noWA || "").trim();
      if (!asli) {
        throw new Error(
          `Nomor WA ${irban.name} masih kosong. Isi dulu lewat menu Pengaturan → Nomor WA Tim.`
        );
      }
      const nomor = normalizeWANumberLocal(asli);
      if (!POLA_NOMOR_WA.test(nomor)) {
        throw new Error(
          `Nomor WA ${irban.name} ("${asli}") tidak berbentuk nomor HP Indonesia yang sah. ` +
            "Perbaiki di menu Pengaturan — formatnya 08xxxxxxxxxx atau 628xxxxxxxxxx."
        );
      }

      setWaTarget({ name: irban.name, role: irban.role, noWAAsli: asli, nomor });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(detail ? formatApiError(detail) : e.message || "Gagal menyiapkan pesan WhatsApp.");
    } finally {
      setWaBusy(false);
    }
  }

  // Baru di sini WhatsApp dibuka — setelah nomor tujuan dilihat & disetujui.
  function bukaWhatsApp() {
    if (!waTarget) return;
    const url = `https://wa.me/${waTarget.nomor}?text=${encodeURIComponent(buildLaporanWA())}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setWaTarget(null);
    setInfoMsg(
      `WhatsApp dibuka untuk ${waTarget.name} (${formatNomorWA(waTarget.nomor)}). ` +
        "Pesan belum terkirim — periksa dulu isinya, lampirkan PDF bila perlu, baru tekan kirim di WhatsApp."
    );
  }

  return (
    <div
      data-testid="chart-dumas-table"
      className="rounded-2xl border border-indigo-100/60 p-5 shadow-sm print:shadow-none relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #eef2ff 0%, #ffffff 55%)" }}
    >
      {/* ── Colored header band ── */}
      <div
        className="-mx-5 -mt-5 px-5 py-4 mb-4 rounded-t-2xl"
        style={{ background: "linear-gradient(135deg, #4338ca 0%, #6366f1 100%)" }}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/20">
              <Mail className="h-5 w-5 text-white" strokeWidth={2} />
            </div>
            <div>
              <h3 className="font-display text-base font-bold text-white leading-tight">
                Surat Masuk Dumas
              </h3>
              <p className="text-[11px] text-indigo-200 mt-0.5">
                Progres penanganan pengaduan masyarakat
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold text-white bg-white/20 px-2.5 py-1 rounded-full">
              {rows.length} surat
            </span>

            {/* Kirim rekap ke Irban lewat WhatsApp */}
            <button
              onClick={handleKirimWA}
              disabled={waBusy || rows.length === 0}
              title="Kirim rekap ke Irban Khusus via WhatsApp (PDF lampiran ikut disiapkan)"
              className="flex items-center gap-1 rounded-full bg-emerald-500 px-2.5 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {waBusy ? (
                <RefreshCw className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
              ) : (
                <MessageCircle className="h-3.5 w-3.5" strokeWidth={2} />
              )}
              <span className="hidden sm:inline">Kirim WA</span>
            </button>

            {/* Cetak / simpan PDF */}
            <button
              onClick={handleCetakPDF}
              disabled={rows.length === 0}
              title="Cetak atau simpan sebagai PDF"
              className="flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-white/30 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Printer className="h-3.5 w-3.5" strokeWidth={2} />
              <span className="hidden sm:inline">PDF</span>
            </button>

            {canUpload && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xlsm"
                  className="hidden"
                  onChange={handleFileSelect}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  title="Unggah Excel (mengganti seluruh data)"
                  className="flex h-7 w-7 items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors disabled:opacity-50"
                >
                  {uploading ? (
                    <RefreshCw className="h-3.5 w-3.5 text-white animate-spin" strokeWidth={2} />
                  ) : (
                    <Upload className="h-3.5 w-3.5 text-white" strokeWidth={2} />
                  )}
                </button>
              </>
            )}
            <button
              onClick={fetchData}
              title="Refresh"
              className="flex h-7 w-7 items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
            >
              <RefreshCw className={`h-3.5 w-3.5 text-white ${loading ? "animate-spin" : ""}`} strokeWidth={2} />
            </button>
          </div>
        </div>
      </div>

      {/* ── Feedback ── */}
      {uploadMsg && (
        <div className="mb-3 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11.5px] text-emerald-700">
          <CheckCircle2 className="h-4 w-4 flex-shrink-0" strokeWidth={2} />
          {uploadMsg}
        </div>
      )}
      {infoMsg && (
        <div className="mb-3 flex items-start gap-2 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 text-[11.5px] text-sky-800">
          <Info className="h-4 w-4 flex-shrink-0 mt-px" strokeWidth={2} />
          <span className="break-words">{infoMsg}</span>
        </div>
      )}
      {error && (
        <div className="mb-3 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[11.5px] text-red-700">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-px" strokeWidth={2} />
          <span className="break-words">{error}</span>
        </div>
      )}

      {/* ── Summary chips ── */}
      <div className="flex flex-wrap gap-1.5 mb-2">
        <button
          onClick={() => setFilter("ALL")}
          className={`px-2.5 py-1 rounded-full text-[10.5px] font-semibold transition-all ${
            filter === "ALL" ? "bg-indigo-600 text-white shadow-sm" : "bg-indigo-50 text-indigo-600 hover:bg-indigo-100"
          }`}
        >
          Semua ({rows.length})
        </button>
        {STATUS_ORDER.map((key) =>
          counts[key] > 0 ? (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10.5px] font-semibold transition-all ${
                STATUS[key].bg
              } ${STATUS[key].text} ${filter === key ? "ring-1 ring-current shadow-sm" : "hover:opacity-80"}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${STATUS[key].dot}`} />
              {STATUS[key].label} ({counts[key]})
            </button>
          ) : null
        )}
      </div>

      {/* ── Rekap sumber surat ── */}
      {rows.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          <span
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10.5px] font-semibold text-rose-700"
            style={{ background: "linear-gradient(135deg, #ffe4e6 0%, #fff1f2 100%)" }}
          >
            <Scale className="h-3 w-3" strokeWidth={2} />
            APH: {sumber.aph}
          </span>
          <span
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10.5px] font-semibold text-sky-700"
            style={{ background: "linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 100%)" }}
          >
            <Building2 className="h-3 w-3" strokeWidth={2} />
            Selain APH: {sumber.nonAph}
          </span>
        </div>
      )}

      {/* ── Search bar ── */}
      <div className="relative mb-3">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" strokeWidth={1.8} />
        <input
          type="text"
          placeholder="Cari objek, asal surat, materi..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-xl border border-indigo-100 bg-white/70 pl-8 pr-3 py-1.5 text-[11.5px] text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-300"
        />
      </div>

      {/* ── Table / States ── */}
      {loading ? (
        <div className="flex flex-col items-center justify-center gap-2 py-12 text-indigo-400">
          <RefreshCw className="h-5 w-5 animate-spin" />
          <span className="text-[12px]">Memuat data…</span>
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
          <FileSpreadsheet className="h-8 w-8 text-indigo-300" strokeWidth={1.5} />
          <div className="text-[12.5px] font-semibold text-slate-600">Belum ada data Dumas</div>
          <div className="text-[11px] text-slate-400 max-w-[240px]">
            {canUpload
              ? "Klik ikon unggah di pojok kanan atas untuk mengunggah file Excel."
              : "Hubungi Admin/IRBAN untuk mengunggah data terbaru."}
          </div>
        </div>
      ) : (
        <div
          className="overflow-auto rounded-xl border border-indigo-100/60"
          style={{ maxHeight: 520, background: "linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)" }}
        >
          <table className="min-w-full table-fixed text-[11px]">
            <colgroup>
              <col style={{ width: "34px" }} />
              <col style={{ width: "78px" }} />
              <col style={{ width: "130px" }} />
              <col style={{ width: "170px" }} />
              <col />
              <col style={{ width: "104px" }} />
            </colgroup>
            <thead
              className="sticky top-0 z-10 text-[9.5px] uppercase tracking-[0.07em] text-indigo-700/80"
              style={{ background: "linear-gradient(135deg, #eef2ff, #e0e7ff)" }}
            >
              <tr>
                <th className="px-2 py-2 text-center font-semibold">No</th>
                <th className="px-2 py-2 text-left font-semibold">Tanggal</th>
                <th className="px-2 py-2 text-left font-semibold">No. Surat</th>
                <th className="px-2 py-2 text-left font-semibold">Objek / Asal Surat</th>
                <th className="px-2 py-2 text-left font-semibold">Materi Pengaduan</th>
                <th className="px-2 py-2 text-left font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-indigo-50/70">
              {groups.map((g) =>
                g.items.length === 0 ? null : (
                  <Fragment key={g.key}>
                    {/* Judul kelompok */}
                    <tr>
                      <td
                        colSpan={6}
                        className="px-2 py-1.5"
                        style={{ background: g.headGradient, borderLeft: `3px solid ${g.accent}` }}
                      >
                        <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.06em] ${g.headText}`}>
                          <g.Icon className="h-3 w-3" strokeWidth={2.2} />
                          {g.label}
                          <span className="font-semibold opacity-70">({g.items.length})</span>
                        </span>
                      </td>
                    </tr>

                    {g.items.map((row, i) => (
                      <tr
                        key={`${g.key}-${row.no}-${i}`}
                        className="align-top transition-opacity hover:opacity-95"
                        style={{ background: ROW_TINT[row.status] || "transparent" }}
                        title={row.keterangan || undefined}
                      >
                        {/* No */}
                        <td className="px-2 py-2 text-center font-mono text-[10px] text-slate-400">{row.no}</td>

                        {/* Tanggal */}
                        <td className="px-2 py-2 text-[10.5px] leading-snug text-slate-500 break-words">
                          {row.tanggal || row.tglTerima}
                        </td>

                        {/* No. Surat */}
                        <td className="px-2 py-2">
                          <span className="block font-mono text-[9.5px] leading-snug text-slate-500 break-words">
                            {row.noSurat}
                          </span>
                        </td>

                        {/* Objek + Asal */}
                        <td className="px-2 py-2">
                          <div className="text-[11px] font-semibold leading-snug text-slate-800 break-words">
                            {row.objek}
                          </div>
                          <div className="mt-0.5 text-[9.5px] leading-snug text-slate-400 break-words">
                            {row.asalSurat}
                          </div>
                        </td>

                        {/* Materi */}
                        <td className="px-2 py-2">
                          <span className="block text-[11px] leading-snug text-slate-700 break-words">
                            {row.materi}
                          </span>
                        </td>

                        {/* Status + Sifat */}
                        <td className="px-2 py-2">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9.5px] font-bold ${STATUS[row.status]?.bg} ${STATUS[row.status]?.text}`}
                          >
                            <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${STATUS[row.status]?.dot}`} />
                            {STATUS[row.status]?.label || row.status}
                          </span>
                          {row.sifat && row.sifat !== "Biasa" && row.sifat !== "-" && (
                            <div className={`mt-0.5 text-[9px] leading-none ${URGENCY_COLOR[row.sifat] || "text-slate-500"}`}>
                              ⚑ {row.sifat}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                )
              )}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-[12px] italic text-slate-400">
                    Tidak ada data yang sesuai
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer: siapa & kapan terakhir upload */}
      {rows.length > 0 && (
        <div className="mt-2.5 flex items-center justify-between flex-wrap gap-1">
          <div className="flex items-center gap-1 text-[10px] text-slate-400">
            <CheckCircle2 className="h-3 w-3 text-emerald-400" strokeWidth={2} />
            {counts.SELESAI + counts.DITANGGAPI} ditangani &nbsp;·&nbsp;
            {counts.PROSES} diproses &nbsp;·&nbsp;
            {counts.DITERIMA + counts.DITANGGUHKAN} pending
          </div>
          {ts && (
            <div className="text-[10px] text-slate-400">
              Diunggah{uploadedBy ? ` oleh ${uploadedBy}` : ""} ·{" "}
              {ts.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" })}
            </div>
          )}
        </div>
      )}

      {/* ── Konfirmasi tujuan WhatsApp ──────────────────────────────────── */}
      {waTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 print:hidden">
          <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div
              className="flex items-center justify-between px-4 py-3"
              style={{ background: "linear-gradient(135deg, #059669 0%, #10b981 100%)" }}
            >
              <div className="flex items-center gap-2 text-white">
                <MessageCircle className="h-4 w-4" strokeWidth={2.2} />
                <span className="text-[13px] font-bold">Periksa tujuan sebelum dikirim</span>
              </div>
              <button
                onClick={() => setWaTarget(null)}
                className="rounded-full p-1 text-white/80 transition-colors hover:bg-white/20 hover:text-white"
                title="Tutup"
              >
                <X className="h-4 w-4" strokeWidth={2.2} />
              </button>
            </div>

            <div className="px-4 py-3">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 px-3 py-2.5">
                <div className="flex items-start gap-2">
                  <ShieldCheck className="mt-px h-4 w-4 flex-shrink-0 text-emerald-600" strokeWidth={2.2} />
                  <div className="text-[11.5px] leading-relaxed text-slate-700">
                    <div>
                      Penerima: <span className="font-bold">{waTarget.name}</span>{" "}
                      <span className="text-slate-500">({waTarget.role})</span>
                    </div>
                    <div>
                      Nomor:{" "}
                      <span className="font-mono font-bold text-emerald-700">
                        {formatNomorWA(waTarget.nomor)}
                      </span>
                      <span className="text-slate-400"> — tertulis "{waTarget.noWAAsli}" di Pengaturan</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-2.5 text-[10.5px] font-semibold uppercase tracking-wide text-slate-400">
                Isi pesan
              </div>
              <pre className="mt-1 max-h-56 overflow-auto whitespace-pre-wrap rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 font-sans text-[11px] leading-relaxed text-slate-700">
                {buildLaporanWA()}
              </pre>

              <div className="mt-2 flex items-start gap-1.5 text-[10.5px] leading-relaxed text-slate-500">
                <Info className="mt-px h-3.5 w-3.5 flex-shrink-0 text-sky-500" strokeWidth={2} />
                <span>
                  WhatsApp akan terbuka dengan pesan ini — <b>belum terkirim</b> sampai kamu menekan
                  tombol kirim di sana. Lampiran PDF (bila perlu) dibuat lewat tombol di bawah, lalu
                  dilampirkan sendiri di chat.
                </span>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 bg-slate-50 px-4 py-2.5">
              <button
                onClick={() => setWaTarget(null)}
                className="rounded-lg px-3 py-1.5 text-[11.5px] font-semibold text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-700"
              >
                Batal
              </button>
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(buildLaporanWA());
                  setInfoMsg("Teks laporan disalin — bisa ditempel manual di WhatsApp bila perlu.");
                }}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11.5px] font-semibold text-slate-600 transition-colors hover:bg-slate-100"
              >
                Salin teks
              </button>
              <button
                onClick={handleCetakPDF}
                className="flex items-center gap-1 rounded-lg border border-indigo-200 bg-white px-3 py-1.5 text-[11.5px] font-semibold text-indigo-600 transition-colors hover:bg-indigo-50"
              >
                <Printer className="h-3.5 w-3.5" strokeWidth={2} />
                Buat PDF lampiran
              </button>
              <button
                onClick={bukaWhatsApp}
                className="flex items-center gap-1 rounded-lg bg-emerald-500 px-3 py-1.5 text-[11.5px] font-bold text-white transition-colors hover:bg-emerald-600"
              >
                <Send className="h-3.5 w-3.5" strokeWidth={2.2} />
                Buka WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
